import React, { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, MapPin, Anchor, CheckCircle, 
  Loader2, Sparkles, ChevronRight, TicketCheck
} from 'lucide-react';
import TabPageLayout from './TabPageLayout';
import { Button } from './ui/button';
import apiV2 from '../utils/apiV2';

const fetcher = (url) => apiV2.get(url).then(r => r.data);

const TZ_MAP = {
  'America/Chicago': 'CST', 'America/New_York': 'EST',
  'America/Los_Angeles': 'PST', 'America/Denver': 'MST',
  'Pacific/Honolulu': 'HST', 'Europe/London': 'GMT',
};

const formatTime = (t, tz) => {
  if (!t) return 'TBD';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  const tzAbbr = TZ_MAP[tz] || tz?.split('/').pop()?.slice(0, 3).toUpperCase() || '';
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}${tzAbbr ? ' '+tzAbbr : ''}`;
};

const formatDate = (d) => {
  if (!d) return 'TBD';
  const dt = new Date(d + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const tom = new Date(today); tom.setDate(today.getDate()+1);
  if (dt.getTime() === today.getTime()) return 'Today';
  if (dt.getTime() === tom.getTime()) return 'Tomorrow';
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const TripCard = ({ trip, onClaim, onUnclaim, claimedByMe }) => {
  const [loading, setLoading] = useState(false);

  const handleClaim = async () => {
    setLoading(true);
    try {
      const res = await onClaim(trip.id);
      if (res.success) {
        toast.success(res.message || "You're on!");
      } else if (res.conflict) {
        toast.error(res.message, { duration: 7000 });
      } else if (res.claimed) {
        toast.error(`Already claimed by ${res.claimedBy}. Only the owner can reassign.`);
      } else {
        toast.error(res.message || 'Could not claim trip');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message;
      toast.error(msg || 'Failed to claim — check your connection and try again', { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleUnclaim = async () => {
    setLoading(true);
    try {
      await onUnclaim(trip.id);
      toast.success('Trip unclaimed');
    } catch {
      toast.error('Failed to unclaim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden border-l-4 transition-all ${
      claimedByMe ? 'border-green-500' : 'border-blue-500'
    }`}>
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{trip.serviceName || 'Trip'}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="font-medium text-gray-700">{formatDate(trip.date)}</span>
              <span>·</span>
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{formatTime(trip.startTime, trip.timezone)}</span>
              {trip.durationMinutes && (
                <><span>·</span><span>{Math.round(trip.durationMinutes/60)}hr</span></>
              )}
            </div>
          </div>
          {claimedByMe && (
            <span className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full flex-shrink-0">
              <CheckCircle className="w-3 h-3" />
              Claimed
            </span>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
          {trip.partySize && (
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-gray-400" />
              <span>{trip.partySize} {trip.partySize === 1 ? 'guest' : 'guests'}</span>
            </div>
          )}
          {trip.assetName && (
            <div className="flex items-center gap-1.5">
              <Anchor className="w-3.5 h-3.5 text-gray-400" />
              <span>{trip.assetName}</span>
            </div>
          )}
          {(trip.city || trip.pickupLocation) && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-gray-400" />
              <span className="truncate max-w-[160px]">{trip.pickupLocation || trip.city}</span>
            </div>
          )}
        </div>

        {/* Slot status */}
        {trip.slotSummary && trip.slotSummary.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {trip.slotSummary.map((slot, i) => (
              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                slot.open > 0 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-green-50 text-green-700'
              }`}>
                {slot.roleName} {slot.filled}/{slot.required}
                {slot.open > 0 ? ' · open' : ' ✓'}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="pt-1">
          {claimedByMe ? (
            <button
              onClick={handleUnclaim}
              disabled={loading}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : 'Unclaim this trip'}
            </button>
          ) : (
            <Button
              onClick={handleClaim}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm font-semibold"
              data-testid={`claim-btn-${trip.confirmationCode}`}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <TicketCheck className="w-4 h-4 mr-2" />
                  Claim this trip
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const AssignmentsTab = () => {
  const [claimedIds, setClaimedIds] = useState(new Set());

  const { data, isLoading, mutate } = useSWR('/aoc/available-trips', fetcher, { refreshInterval: 30000 });
  const { data: unclaimData, mutate: mutateUnclaims } = useSWR('/aoc/unclaim-requests', fetcher, { refreshInterval: 30000 });

  const handleDecideUnclaim = async (reqId, decision) => {
    try {
      await apiV2.post(`/aoc/unclaim-requests/${reqId}/${decision}`);
      toast.success(decision === 'approve' ? 'Unclaim approved — slot is now open' : 'Unclaim denied');
      mutate(); mutateUnclaims();
    } catch { toast.error('Failed to process request'); }
  };

  const unclaimRequests = unclaimData?.requests || [];
  const trips = data?.trips || [];

  // Split: mine, others' claimed, truly available
  const myClaimedTrips = trips.filter(t =>
    t.claimedByMe || claimedIds.has(t.id)
  );
  const claimedByOthers = trips.filter(t =>
    !t.claimedByMe && !claimedIds.has(t.id) && t.claimedBy
  );
  const available = trips.filter(t =>
    !t.claimedByMe && !claimedIds.has(t.id) && !t.claimedBy
  );

  const handleClaim = async (bookingId) => {
    const res = await apiV2.post(`/aoc/claim/${bookingId}`).then(r => r.data);
    if (res.success) {
      setClaimedIds(prev => new Set([...prev, bookingId]));
      mutate();
    }
    return res;
  };

  const handleUnclaim = async (bookingId) => {
    await apiV2.delete(`/aoc/claim/${bookingId}`);
    setClaimedIds(prev => { const s = new Set(prev); s.delete(bookingId); return s; });
    mutate();
  };

  return (
    <TabPageLayout
      title="Assignments"
      subtitle="Available trips to self-assign"
    >
      <div className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
          </div>
        ) : trips.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-blue-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">All caught up!</h3>
            <p className="text-sm text-gray-500">No unassigned trips right now. Check back soon.</p>
          </div>
        ) : (
          <>
        {/* Owner: pending unclaim requests */}
            {unclaimRequests.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-3">
                  Pending Unclaim Requests
                </p>
                <div className="space-y-2">
                  {unclaimRequests.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{r.team_member_name} · {r.service_name}</p>
                        <p className="text-xs text-gray-400">{r.booking_date?.slice(0,10)} · {r.confirmation_code}</p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => handleDecideUnclaim(r.id, 'approve')}
                          className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
                          Approve
                        </button>
                        <button onClick={() => handleDecideUnclaim(r.id, 'deny')}
                          className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My claimed trips */}
            {myClaimedTrips.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Your Trips</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {myClaimedTrips.map(trip => (
                    <TripCard key={trip.id} trip={trip} onClaim={handleClaim} onUnclaim={handleUnclaim} claimedByMe={true} />
                  ))}
                </div>
              </div>
            )}

            {/* Available to claim */}
            {available.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Available · {available.length} trip{available.length !== 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {available.map(trip => (
                    <TripCard key={trip.id} trip={trip} onClaim={handleClaim} onUnclaim={handleUnclaim} claimedByMe={false} />
                  ))}
                </div>
              </div>
            )}

            {/* Claimed by others — visible but locked */}
            {claimedByOthers.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Claimed by Teammates</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {claimedByOthers.map(trip => (
                    <div key={trip.id} className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden border-l-4 border-gray-200 opacity-60">
                      <div className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-700 truncate">{trip.serviceName}</h3>
                            <p className="text-xs text-gray-400 mt-0.5">{formatDate(trip.date)} · {formatTime(trip.startTime, trip.timezone)}</p>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap">
                            {trip.claimedBy}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">Owner can reassign if needed.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {trips.length === 0 && (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-blue-500" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">All caught up!</h3>
                <p className="text-sm text-gray-500">No unassigned trips right now.</p>
              </div>
            )}
          </>
        )}
      </div>
    </TabPageLayout>
  );
};

export default AssignmentsTab;
